'use client';

import clsx from 'clsx';
import { AnimatePresence, LayoutGroup } from 'motion/react';
import * as m from 'motion/react-m';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { FC, useCallback, useEffect, useState } from 'react';

import { IconCaretDown } from 'src/general/Icons/IconCaretDown';

import { LazyMotionWrapperFull } from '../LazyMotionWrapper/LazyMotionWrapperFull';
import { HeaderMenuData, HeaderMenuNavigationItem } from './HeaderMenu.type';
import { SubmenuFaq } from './Submenu/SubmenuFaq';
import { SubmenuWrapper } from './Submenu/SubmenuWrapper';

type Props = {
  headerData: HeaderMenuData;
};

export const HeaderMenuLinks: FC<Props> = ({ headerData }) => {
  const pathname = usePathname();
  const [pathActiveIndex, setPathActiveIndex] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const handleSetActiveIndex = useCallback(
    (index: number | null) => {
      setActiveIndex(index ?? pathActiveIndex);
    },
    [setActiveIndex, pathActiveIndex],
  );

  useEffect(() => {
    let pathActiveIndex: number | null = null;
    if (pathname) {
      pathActiveIndex = headerData.navigation.findIndex((item) => {
        const href = getHref(item);
        if (!href) {
          return false;
        }
        return pathname.startsWith(href);
      });
    }
    setPathActiveIndex(pathActiveIndex);
    setActiveIndex(pathActiveIndex);
  }, [pathname, headerData.navigation]);

  return (
    <LazyMotionWrapperFull>
      <LayoutGroup id="header-menu-link-underline">
        <ul className="hidden items-start justify-start pl-8 md:flex">
          {headerData.navigation.map((item, index) => (
            <HeaderMenuLink
              item={item}
              index={index}
              key={item.label}
              activeIndex={activeIndex}
              setActiveIndex={handleSetActiveIndex}
            />
          ))}
        </ul>
      </LayoutGroup>
    </LazyMotionWrapperFull>
  );
};

type LinkProps = {
  index: number;
  item: HeaderMenuNavigationItem;
  activeIndex: number | null;
  setActiveIndex: (index: number | null) => void;
};

const HeaderMenuLink: FC<LinkProps> = ({ item, index, activeIndex, setActiveIndex }) => {
  const isActive = index === activeIndex;
  const href = getHref(item);

  if (!href) {
    return null;
  }

  return (
    <li
      className="group/header-link relative"
      onMouseEnter={() => setActiveIndex(index)}
      onMouseLeave={() => setActiveIndex(null)}
    >
      <Link
        href={href}
        className={clsx('c-fs-body1 relative flex h-16 items-center justify-center overflow-hidden p-4 no-underline', {
          'pointer-events-none': item.type === 'submenu_faq',
        })}
        data-testid={`header-menu-link-${href}`}
        tabIndex={item.type === 'submenu_faq' ? -1 : undefined}
      >
        {item.label}
        {item.type === 'submenu_faq' && (
          <span className="transition-duration-200 ml-1.5 transition-transform group-hover/header-link:rotate-180">
            <IconCaretDown />
          </span>
        )}
        <AnimatePresence>
          {isActive && (
            <m.div
              className="absolute top-0 left-1/2 h-2 w-12 -translate-1/2 rounded-full bg-current"
              layoutId="header-menu-link-underline"
              transition={{ duration: 0.2 }}
            />
          )}
        </AnimatePresence>
      </Link>

      {item.type === 'submenu_faq' && (
        <SubmenuWrapper>
          <SubmenuFaq submenu={item} />
        </SubmenuWrapper>
      )}
    </li>
  );
};

const getHref = (item: HeaderMenuNavigationItem) => {
  if (item.type === 'link') {
    return item.href;
  } else if (item.type === 'submenu_faq') {
    return item.submenu.href;
  }
  return undefined;
};
